<script setup>
// Tạo tag hàng loạt: dán cả danh sách của một bộ phận vào rồi tạo một lượt,
// thay vì mở form "New label" vài chục lần.
//
// Tạo qua đúng store action `labels/create` sẵn có — không thêm API nào ở
// backend. Chuẩn hoá tên theo ràng buộc label của Chatwoot (thường-hoá, chữ
// unicode + số + gạch; app/models/label.rb + lib/regex_helper.rb).
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import PageConfigsAPI, { loiApi } from 'dashboard/api/pageConfigs';
import {
  loadPageConfigs,
  setPageConfig,
  pageLabelsOf,
} from 'dashboard/helper/pageConfig';
import Button from 'dashboard/components-next/button/Button.vue';
import ChonMuc from 'dashboard/components/ui/SelectMenu.vue';

const emit = defineEmits(['close', 'created']);

const { t } = useI18n();
const store = useStore();
const inboxes = useMapGetter('inboxes/getInboxes');

const vanBan = ref('');
const dangTao = ref(false);
const ketQua = ref('');
// Gắn luôn vào một trang: đúng việc thật — "khai bộ tag cho CSKH" là dán danh
// sách, chọn trang, bấm tạo, xong. Để trống thì chỉ tạo tag ở cấp tài khoản.
const trangGan = ref('');

// Bảng màu xoay vòng cho dòng không ghi màu — sắc độ kiểu Pancake.
const MAU = [
  '#E04B4B',
  '#4AC0B4',
  '#54AECB',
  '#9B6BC9',
  '#D89A5A',
  '#BBC94A',
  '#7C93B5',
  '#A84E4E',
  '#5A6472',
  '#C9A05A',
];

const chuanHoaTen = raw =>
  raw
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^\p{L}\p{N}_-]/gu, '');

const cacDong = computed(() =>
  vanBan.value
    .split('\n')
    .map(l => l.trim())
    .filter(Boolean)
);

const tao = async () => {
  if (!cacDong.value.length) return;
  dangTao.value = true;
  ketQua.value = '';

  const daTao = [];
  const hong = [];
  for (let i = 0; i < cacDong.value.length; i += 1) {
    const [tenTho, mau] = cacDong.value[i]
      .split(',')
      .map(x => (x || '').trim());
    const title = chuanHoaTen(tenTho);
    if (title.length < 2) {
      hong.push(`"${tenTho}" (${t('BONBOZ.TAG_TRANG.BULK_INVALID')})`);
    } else {
      try {
        // Tuần tự, không Promise.all: đọc được kết quả từng dòng, và tránh dội
        // một loạt request tạo trùng khi danh sách dán vào có dòng lặp.
        // eslint-disable-next-line no-await-in-loop
        await store.dispatch('labels/create', {
          title,
          color: /^#[0-9a-fA-F]{6}$/.test(mau) ? mau : MAU[i % MAU.length],
          show_on_sidebar: true,
        });
        daTao.push(title);
      } catch (e) {
        hong.push(`"${title}" (${e.message || t('BONBOZ.TAG_TRANG.EXISTS')})`);
      }
    }
  }

  // Gắn vào trang sau khi đã tạo xong: cộng dồn vào bộ hiện có của trang chứ
  // không ghi đè, nếu không thì khai bộ thứ hai là mất bộ thứ nhất.
  if (trangGan.value && daTao.length) {
    try {
      // `force`: đọc lại bộ hiện có ngay trước khi ghi, tránh đè mất thẻ mà
      // người khác vừa gán cho trang này.
      await loadPageConfigs(true);
      const dangCo = pageLabelsOf(trangGan.value);
      const { data } = await PageConfigsAPI.update(trangGan.value, {
        labels: [...new Set([...dangCo, ...daTao])],
      });
      setPageConfig(trangGan.value, data);
    } catch (e) {
      hong.push(loiApi(e));
    }
  }

  ketQua.value =
    t('BONBOZ.TAG_TRANG.BULK_RESULT', {
      ok: daTao.length,
      tong: cacDong.value.length,
    }) +
    (hong.length
      ? t('BONBOZ.TAG_TRANG.BULK_FAILED', { list: hong.join(', ') })
      : '');
  if (daTao.length) vanBan.value = '';
  dangTao.value = false;
  if (daTao.length && !hong.length) emit('created');
};
</script>

<template>
  <woot-modal show :on-close="() => emit('close')">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header
        :header-title="$t('BONBOZ.TAG_TRANG.BULK_BTN')"
        :header-content="$t('BONBOZ.TAG_TRANG.BULK_TITLE')"
      />
      <div class="flex flex-col gap-3 p-8 pt-4">
        <textarea
          v-model="vanBan"
          rows="7"
          class="w-full rounded-md border border-n-weak bg-n-alpha-1 px-2 py-1.5 text-sm text-n-slate-12 placeholder:text-n-slate-10 focus:border-n-brand focus:outline-none"
          :placeholder="$t('BONBOZ.TAG_TRANG.BULK_PLACEHOLDER')"
        />

        <div class="flex flex-col gap-1 text-sm text-n-slate-11">
          {{ $t('BONBOZ.TAG_TRANG.BULK_ASSIGN_TO') }}
          <ChonMuc
            v-model="trangGan"
            :muc="inboxes"
            hien-avatar
            :all-label="$t('BONBOZ.TAG_TRANG.BULK_ASSIGN_NONE')"
          />
        </div>

        <p v-if="ketQua" class="m-0 text-sm text-n-slate-11">{{ ketQua }}</p>

        <div class="flex justify-end gap-2">
          <Button
            :label="$t('BONBOZ.TAG_TRANG.CANCEL')"
            size="sm"
            slate
            faded
            @click="emit('close')"
          />
          <Button
            :label="
              dangTao
                ? $t('BONBOZ.TAG_TRANG.BULK_CREATING')
                : $t('BONBOZ.TAG_TRANG.BULK_CREATE')
            "
            size="sm"
            :is-loading="dangTao"
            :disabled="!cacDong.length"
            @click="tao"
          />
        </div>
      </div>
    </div>
  </woot-modal>
</template>
